import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Paper,
  Typography,
  Divider,
  Grid,
  Avatar as MuiAvatar,
  Popover,
  makeStyles
} from '@material-ui/core'
import Avatar from 'components/Avatar'
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import communitySlice, {
  listenToComments,
  listenToPostIfFlagged,
  listenToReplies,
  listenToSinglePost
} from 'store/community'
import moment from 'moment'
import DataLoading from 'components/DataLoading'
import { getInitials } from 'utils/getInitials'
import PostCard from './components/PostCard'

const PostFullView = () => {
  const classes = useStyles()
  const { postId } = useParams()
  const dispatch = useDispatch()

  const [likesAnchorEl, setLikesAnchorEl] = useState(null)
  const [selectedCommentLikes, setSelectedCommentLikes] = useState([])
  const [expandedReplies, setExpandedReplies] = useState({})
  const repliesUnsubscribersRef = React.useRef({})
  const unsubscribeRefs = React.useRef({})
  const isMountedRef = React.useRef(true)

  const {
    postTopics,
    loadingState,
    post,
    postLikes: likes,
    postComments: comments,
    postFlags: flags,
    commentReplies
  } = useSelector((state) => state.community)

  useEffect(() => {
    if (!postId) return undefined

    isMountedRef.current = true

    // listenToSinglePost/listenToComments/listenToPostIfFlagged are async thunks, so dispatch()
    // returns a Promise resolving to the unsubscribe function, not the function itself. If the
    // component unmounts before these resolve, call the unsubscribe as soon as it arrives instead
    // of leaking the listener.
    Promise.resolve(dispatch(listenToSinglePost(postId))).then((unsub) => {
      if (!isMountedRef.current) {
        typeof unsub === 'function' && unsub()
      } else {
        unsubscribeRefs.current.post = unsub
      }
    })
    Promise.resolve(dispatch(listenToComments(postId))).then((unsub) => {
      if (!isMountedRef.current) {
        typeof unsub === 'function' && unsub()
      } else {
        unsubscribeRefs.current.comments = unsub
      }
    })
    Promise.resolve(dispatch(listenToPostIfFlagged(postId))).then((unsub) => {
      if (!isMountedRef.current) {
        typeof unsub === 'function' && unsub()
      } else {
        unsubscribeRefs.current.flags = unsub
      }
    })

    return () => {
      isMountedRef.current = false
      dispatch(communitySlice.actions.resetPost())
      Object.values(unsubscribeRefs.current).forEach(
        (unsub) => typeof unsub === 'function' && unsub()
      )
      unsubscribeRefs.current = {}
      Object.values(repliesUnsubscribersRef.current).forEach(
        (unsub) => typeof unsub === 'function' && unsub()
      )
      repliesUnsubscribersRef.current = {}
    }
  }, [dispatch, postId])

  const handleOpenCommentLikes = (event, likedBy) => {
    setLikesAnchorEl(event.currentTarget)
    setSelectedCommentLikes(likedBy || [])
  }

  const handleCloseCommentLikes = () => {
    setLikesAnchorEl(null)
    setSelectedCommentLikes([])
  }

  const handleToggleReplies = (commentId) => {
    const isExpanded = !!expandedReplies[commentId]

    if (isExpanded) {
      setExpandedReplies((prev) => ({ ...prev, [commentId]: false }))
      return
    }

    if (!repliesUnsubscribersRef.current[commentId]) {
      Promise.resolve(dispatch(listenToReplies(postId, commentId))).then((unsub) => {
        if (!isMountedRef.current) {
          typeof unsub === 'function' && unsub()
        } else {
          repliesUnsubscribersRef.current[commentId] = unsub
        }
      })
    }

    setExpandedReplies((prev) => ({ ...prev, [commentId]: true }))
  }

  if (loadingState.post)
    return (
      <Box p={4}>
        <DataLoading />
      </Box>
    )
  if (!post) return <Box p={4}>Post not found</Box>

  return (
    <Box maxWidth="1100px" mx="auto" mt={4}>
      <Grid container spacing={4}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Post Card */}

          <PostCard
            post={post || {}}
            postTopics={postTopics}
            conferenceId={post?.conferenceId}
            cardType="expanded"
          />
          {/* Comments Section */}
          <Paper elevation={1} style={{ marginTop: 20, borderRadius: 16, padding: 20 }}>
            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 16 }}>
              Comments ({comments?.length})
            </Typography>
            {comments?.length === 0 ? (
              <Typography color="textSecondary" style={{ textAlign: 'center' }}>
                No comments yet.
              </Typography>
            ) : (
              comments?.map((comment, idx) => (
                <Box key={idx} mb={2} style={{ display: 'flex', gap: 10 }}>
                  {post?.conferenceId && comment?.author?.profilePicture ? (
                    <Avatar
                      conferenceId={post?.conferenceId}
                      imageName={comment?.author?.profilePicture}
                      path="attendees"
                      height={24}
                      width={24}
                      progressSize={16}
                      className={classes.avatarImage}
                    />
                  ) : (
                    <MuiAvatar className={classes.avatar}>
                      {getInitials(comment?.author?.name)}
                    </MuiAvatar>
                  )}
                  <Paper elevation={0} className={classes.commentCard}>
                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography style={{ fontWeight: 600 }}>{comment?.author?.name}</Typography>
                      <span className={classes.metadataDot}>•</span>
                      <Typography variant="caption" color="textSecondary">
                        {moment(comment.createdAt).fromNow()}
                      </Typography>
                    </Box>
                    <Typography variant="body2">{comment.content}</Typography>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {/* Comment likes */}
                      {(comment?.likes > 0 || comment?.likedBy?.length > 0) && (
                        <Box
                          className={classes.commentLikes}
                          onClick={(e) => handleOpenCommentLikes(e, comment.likedBy)}>
                          <ThumbUpOutlinedIcon style={{ fontSize: 14 }} />
                          <Typography variant="caption">
                            {comment.likes || comment.likedBy?.length || 0}
                          </Typography>
                        </Box>
                      )}
                      {/* Replies toggle */}
                      {comment?.noOfReplies > 0 && (
                        <Box
                          className={classes.repliesToggle}
                          onClick={() => handleToggleReplies(comment.id)}>
                          <Typography variant="caption">
                            {expandedReplies[comment.id]
                              ? 'Hide replies'
                              : `View replies (${comment.noOfReplies})`}
                          </Typography>
                          {expandedReplies[comment.id] ? (
                            <ExpandLessIcon style={{ fontSize: 16 }} />
                          ) : (
                            <ExpandMoreIcon style={{ fontSize: 16 }} />
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* Replies list */}
                    {expandedReplies[comment.id] && (
                      <Box className={classes.repliesContainer}>
                        {!commentReplies?.[comment.id] ? (
                          <Typography variant="caption" color="textSecondary">
                            Loading replies...
                          </Typography>
                        ) : (
                          commentReplies[comment.id].map((reply, replyIdx) => (
                            <Box key={replyIdx} mb={1.5} style={{ display: 'flex', gap: 8 }}>
                              {post?.conferenceId && reply?.author?.profilePicture ? (
                                <Avatar
                                  conferenceId={post?.conferenceId}
                                  imageName={reply?.author?.profilePicture}
                                  path="attendees"
                                  height={20}
                                  width={20}
                                  progressSize={14}
                                  className={classes.avatarImage}
                                />
                              ) : (
                                <MuiAvatar className={classes.replyAvatar}>
                                  {getInitials(reply?.author?.name)}
                                </MuiAvatar>
                              )}
                              <Paper elevation={0} className={classes.replyCard}>
                                <Box style={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography style={{ fontWeight: 600, fontSize: 13 }}>
                                    {reply?.author?.name}
                                  </Typography>
                                  <span className={classes.metadataDot}>•</span>
                                  <Typography variant="caption" color="textSecondary">
                                    {moment(reply.createdAt).fromNow()}
                                  </Typography>
                                </Box>
                                <Typography variant="body2">{reply.content}</Typography>
                                {(reply?.likes > 0 || reply?.likedBy?.length > 0) && (
                                  <Box
                                    className={classes.commentLikes}
                                    onClick={(e) => handleOpenCommentLikes(e, reply.likedBy)}>
                                    <ThumbUpOutlinedIcon style={{ fontSize: 12 }} />
                                    <Typography variant="caption">
                                      {reply.likes || reply.likedBy?.length || 0}
                                    </Typography>
                                  </Box>
                                )}
                              </Paper>
                            </Box>
                          ))
                        )}
                      </Box>
                    )}
                  </Paper>
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        {/* Sidebar: Liked By */}
        <Grid item xs={12} md={4}>
          <Paper elevation={1} style={{ borderRadius: 16, padding: 24, marginTop: 0 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
              Liked by ({likes?.length || 0})
            </Typography>
            <Divider style={{ marginBottom: 12 }} />
            {!likes?.length ? (
              <Typography color="textSecondary" style={{ textAlign: 'center' }}>
                No likes yet.
              </Typography>
            ) : (
              likes?.map((user, idx) => (
                <Box
                  key={idx}
                  style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: 12 }}>
                  {user?.profilePicture ? (
                    <Avatar
                      conferenceId={post?.conferenceId}
                      imageName={user?.profilePicture}
                      path="attendees"
                      height={28}
                      width={28}
                      progressSize={16}
                      className={classes.avatarImage}
                    />
                  ) : (
                    <MuiAvatar className={classes.avatar}>{getInitials(user?.name)}</MuiAvatar>
                  )}
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {user.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {user.credential || user.position || 'Attendee'}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Paper>

          <Paper elevation={1} style={{ borderRadius: 16, padding: 20, marginTop: 20 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
              Flagged by ({flags?.length || 0})
            </Typography>
            <Divider style={{ marginBottom: 12 }} />
            {!flags?.length ? (
              <Typography color="textSecondary" style={{ textAlign: 'center' }}>
                No user flagged yet.
              </Typography>
            ) : (
              flags?.map((flag, idx) => (
                <Box key={idx} mb={2} style={{ display: 'flex', gap: 10 }}>
                  {post?.conferenceId && flag?.flaggedBy?.profilePicture ? (
                    <Avatar
                      conferenceId={post?.conferenceId}
                      imageName={flag?.flaggedBy?.profilePicture}
                      path="attendees"
                      height={24}
                      width={24}
                      progressSize={16}
                      className={classes.avatarImage}
                    />
                  ) : (
                    <MuiAvatar className={classes.avatar}>
                      {getInitials(flag?.flaggedBy?.name)}
                    </MuiAvatar>
                  )}
                  <Paper elevation={0} className={classes.commentCard}>
                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography style={{ fontWeight: 600 }}>{flag?.flaggedBy?.name}</Typography>
                      <span className={classes.metadataDot}>•</span>
                      <Typography variant="caption" color="textSecondary">
                        {moment(flag?.createdAt).fromNow()}
                      </Typography>
                    </Box>
                    <Typography variant="body2">{flag.reason || 'N/A'}</Typography>
                  </Paper>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Comment likes popover */}
      <Popover
        open={Boolean(likesAnchorEl)}
        anchorEl={likesAnchorEl}
        onClose={handleCloseCommentLikes}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.likesPopover }}>
        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
          Liked by
        </Typography>
        {selectedCommentLikes.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No likes data available.
          </Typography>
        ) : (
          selectedCommentLikes.map((liker, idx) => (
            <Box key={idx} className={classes.likerItem}>
              <MuiAvatar className={classes.likerAvatar}>{getInitials(liker?.name)}</MuiAvatar>
              <Box>
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {liker.name}
                </Typography>
                {liker.position && (
                  <Typography variant="caption" color="textSecondary">
                    {liker.position}
                  </Typography>
                )}
              </Box>
            </Box>
          ))
        )}
      </Popover>
    </Box>
  )
}

export default PostFullView

const useStyles = makeStyles((theme) => ({
  avatar: {
    width: 28,
    height: 28,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: '13px',
    fontWeight: 'bold',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
  },
  avatarImage: {
    borderRadius: '50%'
  },
  repliesToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    marginTop: 8,
    color: theme.palette.primary.main,
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  repliesContainer: {
    marginTop: 12,
    paddingLeft: 16,
    borderLeft: `2px solid ${theme.palette.divider}`
  },
  replyCard: {
    width: '100%',
    gap: '10px',
    padding: '8px 12px',
    borderRadius: 10,
    background: '#fafafa'
  },
  replyAvatar: {
    width: 20,
    height: 20,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: '10px',
    fontWeight: 'bold'
  },
  metadataDot: {
    fontSize: '10px',
    color: theme.palette.text.secondary,
    margin: '0 4px'
  },
  commentCard: {
    width: '100%',
    gap: '14px',
    padding: '12px 16px',
    borderRadius: 12,
    background: '#f5f5f5'
  },
  commentLikes: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 8,
    padding: '2px 8px',
    borderRadius: 12,
    backgroundColor: '#e8e8e8',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#d9d9d9'
    }
  },
  likesPopover: {
    padding: theme.spacing(2),
    maxWidth: 280,
    maxHeight: 300,
    overflowY: 'auto'
  },
  likerItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
    '&:last-child': {
      marginBottom: 0
    }
  },
  likerAvatar: {
    width: 24,
    height: 24,
    fontSize: '11px',
    fontWeight: 'bold',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  }
}))
